<template>
    <v-dialog v-model="dialog" max-width="750px" @click:outside="hide">
        <v-card class="card-container">
            <v-card-title class="title-container">
                <v-icon color="#FFFFFF" @click="hide" large class="mr-3">mdi-close-circle-outline</v-icon>
                {{ $t('routines.copy_routine') }}
            </v-card-title>
            <v-card-text class="mt-3">
                <v-row dense>
                    <v-col cols="5">
                        <kimsa-combobox
                            :label="$t('routines.date_from_copy')"
                            :placeholder="`01-01-${new Date().getFullYear()}`"
                            outlined dense
                            :items="dates"
                            @change="selectDate = $event"
                            item-text="date"
                            item-value="assignedRoutineId"
                            ref="dates"
                        />
                    </v-col>
                    <v-col cols="5">
                        <kimsa-combobox
                            :label="$t('common.copy_to')"
                            outlined dense
                            :items="copyToOptions"
                            :value="copyTo"
                            @change="copyTo = $event"
                            no-object
                            ref="copyTo"
                        />
                    </v-col>
                    <v-col cols="2">
                        <div class="label mb-n3"> {{ $t('routines.multiple') }}</div>
                        <v-switch hide-details inset v-model="multiple" color="secondary"/>
                    </v-col>
                    <template v-if="multiple">
                        <v-col cols="6">
                            <kimsa-date-picker
                                :label="$t('routines.from')"
                                solo flat
                                dense outlined
                                color="secondary"
                                :max="endDate"
                                @change="startDate = $event"
                                ref="start"
                            />
                        </v-col>
                        <v-col cols="6">
                            <kimsa-date-picker
                                :disabled="!startDate"
                                :label="$t('routines.to')"
                                solo flat
                                dense outlined
                                color="secondary"
                                :min="startDate"
                                @change="endDate = $event"
                                ref="end"
                            />
                        </v-col>
                        <v-col cols="12">
                            <div class="row mx-auto justify-space-between">
                                <v-checkbox v-for="(day,i) in days" :key="i" :label="day.name" v-model="days[i].active"
                                            hide-details color="secondary"/>
                            </div>
                        </v-col>
                    </template>
                    <template v-else>
                        <v-col cols="12">
                            <kimsa-date-picker
                                :label="$t('routines.date_to_copy')"
                                solo flat
                                dense outlined
                                color="secondary"
                                @change="startDate = $event"
                                :allowed-dates="datesAllowed"
                                ref="start"
                            />
                        </v-col>
                    </template>
                    <template v-if="copyTo !== 'self'">
                        <v-col cols="12">
                            <kimsa-combobox
                                searchable
                                :label="labelOptions"
                                :placeholder="$t('common.select_placeholder')"
                                outlined dense
                                :items="optionsFiltered"
                                @change="onChangeSetOption($event)"
                                :value="currentValues"
                                :multiple-with-chips="copyTo !== 'teams'"
                                ref="options"
                            />
                        </v-col>
                        <v-col cols="12" v-if="copyTo === 'teams'">
                            <kimsa-combobox
                                searchable
                                :label="$t('common.subgroups')"
                                :placeholder="$t('common.select_placeholder')"
                                outlined dense
                                :items="subgroupsTeamOptions"
                                @change="setCurrentTeamGroup($event)"
                                :value="subgroups"
                                :readonly="!team"
                                multiple-with-chips
                                ref="subgroup"
                            />
                        </v-col>
                    </template>
                </v-row>
            </v-card-text>
            <v-card-actions class="px-0 pb-0">
                <v-row no-gutters>
                    <v-col cols="3">
                        <v-btn block depressed x-large class="normal-btn no-radius" :disabled="loading" @click="reset">
                            {{ $t('routines.reset') }}
                        </v-btn>
                    </v-col>
                    <v-col cols="9">
                        <v-btn block depressed x-large color="primary" class="normal-btn no-radius black--text"
                               :disabled="disabled" @click="checkAssignRoutines" :loading="loading">
                            {{ $t('routines.copy') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-actions>

            <select-replace-assigned-routines-dates-dialog
                :assigned-routines-in-same-date="assignedRoutinesInSameDate"
                no-close-dialog
                @confirm="onConfirmReplace($event)"
                ref="replaceRoutinesDates"
            />
        </v-card>
    </v-dialog>
</template>

<script>
import {mapMutations, mapState} from "vuex";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaDatePicker from "@/components/common/inputs/KimsaDatePicker";
import {db, Timestamp} from "@/utils/Firebase";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import {
    addAssignedRoutinesGroup, clearBlocksAssignedRoutine,
    deleteAssignedRoutinesGroup, deleteAssignedRoutineStudentInDate, getPayloadBaseAssignedRoutine,
    hasAssignedRoutineInDate,
    hasGroupAssignedRoutineInDate
} from "@/utils/Exercises";
import SelectReplaceAssignedRoutinesDatesDialog
    from "@/components/coach/routines/SelectReplaceAssignedRoutinesDatesDialog";

export default {
    name: "CopyRoutineDialog",
    components: {SelectReplaceAssignedRoutinesDatesDialog, KimsaConfirm, KimsaDatePicker, KimsaCombobox},
    props: {
        studentsOptions: {
            type: Array,
            default: () => [],
        },
        groupsOptions: {
            type: Array,
            default: () => [],
        },
        teamsOptions: {
            type: Array,
            default: () => [],
        },
        currentSelection: {
            type: Object,
            default: null,
        },
        dates: {
            type: Array,
            default: () => [],
        },
        coachId: {
            type: String,
            default: null
        }
    },
    data(vm) {
        return {
            dialog: false,
            selectDate: null,
            multiple: false,
            copyToOptions: [
                {name: vm.$t('common.self'), value: 'self'},
                {name: vm.$t('common.other_students'), value: 'students'},
                {name: vm.$t('common.other_groups'), value: 'groups'},
                {name: vm.$t('common.other_teams'), value: 'teams'},
            ],
            copyTo: 'self',
            students: [],
            groups: [],
            team: null,
            subgroups: [],
            startDate: '',
            endDate: '',
            loading: false,
            days: [
                {name: vm.$t('common.short_monday'), value: 1, active: false},
                {name: vm.$t('common.short_tuesday'), value: 2, active: false},
                {name: vm.$t('common.short_wednesday'), value: 3, active: false},
                {name: vm.$t('common.short_thursday'), value: 4, active: false},
                {name: vm.$t('common.short_friday'), value: 5, active: false},
                {name: vm.$t('common.short_saturday'), value: 6, active: false},
                {name: vm.$t('common.short_sunday'), value: 0, active: false},
            ],
            assignedRoutinesInSameDate: [],
            datesToSkip: [],
        }
    },
    computed: {
        ...mapState(['dialogActive']),
        disabled(vm = this) {
            if (!vm.selectDate || !vm.startDate) return true
            if (vm.multiple && (!vm.endDate || vm.days.filter(day => day.active).length === 0)) return true
            if (vm.copyTo !== 'self' && (!vm.currentValues || vm.currentValues.length === 0)) return true
            if (vm.copyTo === 'teams' && vm.subgroups.length === 0) return true
            return false
        },
        currentValues(vm = this) {
            if (vm.copyTo === 'teams') return vm.team
            return vm[vm.copyTo]
        },
        optionsFiltered(vm = this) {
            if (vm.copyTo === 'groups') return vm.groupsOptionsFiltered
            if (vm.copyTo === 'teams') return vm.teamsOptions
            return vm.studentsOptionsFiltered
        },
        studentsOptionsFiltered(vm = this) {
            return vm.studentsOptions?.filter(student => student.objectID !== vm.currentSelection.objectID || null) || []
        },
        groupsOptionsFiltered(vm = this) {
            return vm.groupsOptions?.filter(group => group.id !== vm.currentSelection.id || null) || []
        },
        subgroupsTeamOptions(vm = this) {
            if (vm.team?.id === vm.currentSelection?.teamId)
                return vm.team?.groups?.filter(subgroup => subgroup.id !== vm.currentSelection.id || null) || []
            return vm.team?.groups || []
        },
        groupTeamOptionsFiltered(vm = this) {
            return vm.groupsOptions?.filter(group => group.id !== vm.currentSelection.id || null) || []
        },
        labelOptions(vm = this) {
            return vm.$t(`common.${vm.copyTo}`)
        },
        currentSelectionType(vm = this) {
            if (!!vm.currentSelection?.teamId) return 'team'
            if (!!vm.currentSelection?.students) return 'group'
            return 'student'
        },
        destinyToCopy(vm = this) {
            if (vm.copyTo === 'self') return [vm.currentSelection]
            if (vm.copyTo === 'students') return vm.students
            if (vm.copyTo === 'groups') return vm.groups
            if (vm.copyTo === 'teams') return vm.subgroups.map(sg => ({teamId: vm.team.id, ...sg}))
        },
        isGroup(vm = this) {
            return (vm.copyTo === 'self' && vm.currentSelectionType !== 'student') || (vm.copyTo === 'groups' || vm.copyTo === 'teams')
        },
        collectionName(vm = this) {
            return vm.currentSelectionType === 'student' ? 'assignedRoutines' : 'groupAssignedRoutines'
        }
    },
    methods: {
        ...mapMutations(['setDialogActive']),
        show(date = null) {
            const vm = this
            vm.dialog = true
            vm.setDialogActive(true)
            vm.setDate(date)
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.setDialogActive(false)
            vm.reset()
        },
        reset() {
            const vm = this
            vm.selectDate = null
            vm.multiple = false
            vm.copyTo = 'self'
            vm.students = []
            vm.groups = []
            vm.team = null
            vm.subgroups = []
            vm.startDate = ''
            vm.endDate = ''
            vm.$nextTick(() => {
                if (vm.$refs.copyTo) vm.$refs.copyTo.model = 'self'
                if (vm.$refs.dates) vm.$refs.dates.model = null
                if (vm.$refs.start) vm.$refs.start.model = null
                if (vm.$refs.options) vm.$refs.options.model = []
                if (vm.$refs.subgroups) vm.$refs.subgroups.model = []
                if (vm.$refs.end) vm.$refs.end.model = null
            })
        },
        setDate(date) {
            const vm = this
            if (!date) return
            let dateFormatted = vm.$moment(date).format('DD-MM-YYYY')
            let dateSelected = vm.dates.find(d => d.date === dateFormatted)
            vm.$nextTick(() => vm.$refs.dates.model = dateSelected || null)
        },
        onChangeSetOption(options) {
            const vm = this
            console.log('options', options)
            if (vm.copyTo === 'teams') return vm.setCurrentTeam(options)
            if (vm.copyTo === 'groups') return vm.setCurrentGroups(options)
            return vm.setStudents(options)
        },
        setStudents(students) {
            const vm = this
            if (students?.length === 0) return vm.students = students
            let currentStudents = [...students]
            let lastStudent = currentStudents[currentStudents.length - 1]
            currentStudents = currentStudents.filter(st => typeof st !== 'string')

            if (typeof lastStudent === 'string') {
                let foundStudent = vm.studentsOptionsFiltered.find(student => student.name.toLowerCase() === lastStudent.toLowerCase()) || null
                foundStudent ? vm.students.push(foundStudent) : vm.students = vm.students.filter(st => typeof st !== 'string')
            } else vm.students = students
        },
        setCurrentGroups(groups) {
            const vm = this
            if (groups?.length === 0) return vm.groups = groups
            let currentGroups = [...groups]
            let lastGroup = currentGroups[currentGroups.length - 1]
            currentGroups = currentGroups.filter(gp => typeof gp !== 'string')

            if (typeof lastGroup === 'string') {
                let foundGroup = vm.groupsOptionsFiltered?.find(group => group.name.toLowerCase() === lastGroup?.toLowerCase()) || null
                foundGroup ? vm.groups.push(foundGroup) : vm.groups = vm.groups.filter(gp => typeof gp !== 'string')
            } else vm.groups = groups
        },
        setCurrentTeam(team) {
            const vm = this
            if (team?.name) return vm.team = team
            if (team) {
                let foundTeam = vm.teamsOptions?.find(tm => tm.name.toLowerCase() === team.toLowerCase()) || null
                vm.team = foundTeam || null
            } else vm.team = team
        },
        setCurrentTeamGroup(subgroups) {
            const vm = this
            if (subgroups?.length === 0) return vm.subgroups = subgroups
            let currentSubgroups = [...subgroups]
            let lastSubgroup = currentSubgroups[currentSubgroups.length - 1]
            currentSubgroups = currentSubgroups.filter(sbg => typeof sbg !== 'string')

            if (typeof lastSubgroup === 'string') {
                let foundSubgroup = vm.subgroupsTeamOptions?.find(sbg => sbg.name.toLowerCase() === lastSubgroup?.toLowerCase()) || null
                foundSubgroup ? vm.subgroups.push(foundSubgroup) : vm.subgroups = vm.subgroups.filter(sbg => typeof sbg !== 'string')
            } else vm.subgroups = subgroups
        },
        daysToCopy() {
            const vm = this
            if (!vm.multiple) return [vm.startDate]

            let currentDay = vm.$moment(vm.startDate)
            const lastDay = vm.$moment(vm.endDate)
            const dates = []
            const days = [...vm.days].sort((a, b) => a.value - b.value)

            while (currentDay.isSameOrBefore(lastDay)) {
                if (days[currentDay.day()].active) dates.push(currentDay.format('YYYY-MM-DD'))
                currentDay.add(1, 'days')
            }

            return dates
        },
        async populateAssignedRoutineInDate() {
            const vm = this
            let assignedRoutinesInSameDate = []

            let range = vm.daysToCopy()
            for (let date of range) {
                for (let currentItem of vm.destinyToCopy) {
                    let assignedRoutineInDate = vm.isGroup ?
                        await hasGroupAssignedRoutineInDate(date, currentItem.id, currentItem.teamId) :
                        await hasAssignedRoutineInDate(currentItem.objectID, date)
                    if (assignedRoutineInDate.has) {
                        assignedRoutinesInSameDate.push({
                            assignedRoutineId: assignedRoutineInDate.id,
                            date: vm.$moment(date).format('DD-MM-YYYY'),
                            assignedRoutinesIds: assignedRoutineInDate.assignedRoutinesIds || null,
                            name: currentItem.name,
                            replace: false,
                        })
                    }
                }
            }

            vm.assignedRoutinesInSameDate = assignedRoutinesInSameDate
        },
        async checkAssignRoutines() {
            const vm = this
            vm.loading = true

            await vm.populateAssignedRoutineInDate()

            if (vm.assignedRoutinesInSameDate.length === 0) return await vm.copyRoutine()
            vm.$refs.replaceRoutinesDates.show()
        },
        async onConfirmReplace(toReplace) {
            const vm = this
            let datesToSkip = []

            for (let routine of toReplace) {
                if (routine.replace) {
                    if (routine.assignedRoutinesIds && vm.isGroup) {
                        await deleteAssignedRoutinesGroup(routine.assignedRoutinesIds)
                        await db.collection('groupAssignedRoutines').doc(routine.assignedRoutineId).delete()
                    } else await db.collection('assignedRoutines').doc(routine.assignedRoutineId).delete()
                } else
                    datesToSkip.push({
                        date: routine.date,
                        name: routine.name,
                    })
            }

            vm.datesToSkip = datesToSkip
            await vm.copyRoutine()
        },
        async copyRoutine() {
            const vm = this
            vm.loading = true
            let assignedRoutineData = null

            if (vm.selectDate.assignedRoutineId) {
                const assignedRoutineRef = await db.collection(vm.collectionName).doc(vm.selectDate.assignedRoutineId).get()
                assignedRoutineData = assignedRoutineRef.data()
            }

            if (!assignedRoutineData) {
                vm.loading = false
                vm.toast(vm.$t('routines.error_coping_routine'), 'error')
                return vm.hide()
            }

            let blocks = clearBlocksAssignedRoutine(assignedRoutineData.blocks)
            let dateNameSet = new Set(vm.datesToSkip.map(item => JSON.stringify(item)))
            let assignedRoutines = []
            let range = vm.daysToCopy()
            for (let date of range) {
                const formattedDate = vm.$moment(date).format('DD-MM-YYYY')
                for (let currentItem of vm.destinyToCopy) {
                    if (!dateNameSet.has(JSON.stringify({date: formattedDate, name: currentItem.name}))) {
                        let payload = getPayloadBaseAssignedRoutine(`${currentItem.name} - ${formattedDate}`, date, assignedRoutineData, blocks)

                        if (currentItem.objectID) {
                            payload.studentId = currentItem.objectID
                        } else {
                            payload.groupId = currentItem.id
                            payload.teamId = currentItem.teamId || null
                        }

                        let collection = !!currentItem.objectID ? 'assignedRoutines' : 'groupAssignedRoutines'

                        let assignedRoutineCreated = await db.collection(collection).add(payload)

                        if (collection === 'groupAssignedRoutines') {
                            for (let student of currentItem.students) await deleteAssignedRoutineStudentInDate(student, date)
                            await addAssignedRoutinesGroup({id: assignedRoutineCreated.id, ...payload, date: date}, currentItem.students)
                        }

                        if (vm.copyTo === 'self') {
                            assignedRoutines.push({
                                ...payload,
                                date: date,
                                id: assignedRoutineCreated.id
                            })
                        }
                    }
                }
            }

            vm.loading = false
            vm.$emit('success', assignedRoutines)
            vm.hide()
            vm.reset()
        },
        datesAllowed(date) {
            const vm = this
            if (vm.copyTo !== 'self') return true
            let dates = vm.dates.map(item => {
                const [day, month, year] = item.date.split('-')
                return `${year}-${month}-${day}`
            })
            return !dates.includes(date)
        },
    },
    watch: {
        dialogActive(val) {
            if (!val) this.hide()
        },
    }
}
</script>

<style scoped>

.card-container {
    border-radius: 8px;
    overflow: hidden;
}

.title-container {
    color: #FFFFFF;
    background: var(--v-backGroundPrimary-base);
}

</style>